import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { DeviceHealthTimelineEvent, DeviceHealthWindow } from '@platform/shared-types';
import { deviceHealthApi } from '../../../services/deviceHealthApi';
import { RubyLoader } from '../../../components/RubyLoader';
import { fmtAgo } from '../../../components/deviceHealth/healthPanelModel';

const LABEL: Record<string, string> = {
  'crash.native': 'App crash', 'crash.exit': 'App exited', 'crash.renderer': 'Screen crash',
  'js.error': 'Script error', 'js.rejection': 'Script error', 'net.fail': 'Server error',
  'print.fail': 'Printer', 'usb.drop': 'USB unplugged', 'pay.anomaly': 'Payment',
  warn: 'Warning', 'session.start': 'App started', 'session.planned_restart': 'Planned restart',
};
const chip = (k: string) =>
  k.startsWith('crash.') || k === 'pay.anomaly' ? 'bg-red-50 text-red-700 border-red-200'
    : k === 'print.fail' || k === 'net.fail' || k === 'usb.drop' ? 'bg-amber-50 text-amber-800 border-amber-200'
      : 'bg-stone-50 text-stone-600 border-stone-200';
const WINDOWS: DeviceHealthWindow[] = ['24h', '7d', '30d'];
/** House rule: an embedded list caps at 5, with a link/control out to the rest. */
const VISIBLE_CAP = 5;

/** The device's own event history (events table, 30-day TTL). Heartbeats are filtered server-side. */
export default function DeviceTimeline({ deviceId }: { deviceId: string }) {
  const [win, setWin] = useState<DeviceHealthWindow>('24h');
  const [events, setEvents] = useState<DeviceHealthTimelineEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let live = true;
    setEvents(null); setError(null); setExpanded(false);
    deviceHealthApi.timeline(deviceId, win)
      .then((e) => { if (live) setEvents([...e].sort((a, b) => b.ts - a.ts)); })
      .catch(() => { if (live) setError('The timeline could not be loaded.'); });
    return () => { live = false; };
  }, [deviceId, win]);

  const visible = events ? (expanded ? events : events.slice(0, VISIBLE_CAP)) : [];
  const hiddenCount = events ? events.length - visible.length : 0;

  return (
    <section className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="text-[14.5px] font-bold tracking-tight text-stone-900">Event timeline</div>
        <div className="flex gap-2">
          {WINDOWS.map((w) => (
            <button key={w} type="button" onClick={() => setWin(w)}
              className={`min-h-[44px] min-w-[44px] rounded-full px-4 text-[13px] border ${w === win ? 'bg-emerald-600 border-emerald-600 text-white font-semibold' : 'bg-white border-stone-300 text-stone-600'}`}>{w}</button>
          ))}
        </div>
      </div>
      {error && <div className="text-[13px] text-red-700">{error}</div>}
      {!error && events === null && <RubyLoader variant="section" className="py-8" />}
      {events && events.length === 0 && <div className="text-[13px] text-stone-500">No events in this window.</div>}
      {events && events.length > 0 && (
        <ol className="divide-y divide-stone-100">
          {visible.map((e) => (
            <li key={e.eventId} className="flex min-h-[44px] flex-wrap items-center gap-2 py-2 text-[13px]">
              <span className="w-20 shrink-0 text-stone-500 tabular-nums">{fmtAgo(e.ts)}</span>
              <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${chip(e.kind)}`}>{LABEL[e.kind] || e.kind}</span>
              {e.issueId
                ? <Link to={`/app-issues/${e.issueId}`} className="font-semibold text-stone-900 hover:text-red-700">{e.message}</Link>
                : <span className="text-stone-800">{e.message}</span>}
              {e.count > 1 && <span className="text-stone-500">×{e.count}</span>}
              <span className="ml-auto text-[11px] text-stone-400">{e.screen} · {e.appVersion}</span>
            </li>
          ))}
        </ol>
      )}
      {hiddenCount > 0 && (
        <button type="button" onClick={() => setExpanded(true)}
          className="mt-3 min-h-[44px] w-full rounded-xl border border-stone-200 bg-white text-[13px] font-semibold text-stone-700 hover:bg-stone-50">
          {`Show all ${events!.length}`}
        </button>
      )}
    </section>
  );
}
